import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, FileText, DollarSign, CreditCard,
  AlertCircle, Check, Clock, ExternalLink
} from 'lucide-react';
import clientPortalService from '../../services/clientPortalService';

const PortalInvoices = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState({ total: 0, total_outstanding: 0, total_overdue: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [filter, setFilter] = useState('all');
  const [processingPayment, setProcessingPayment] = useState(false);

  useEffect(() => {
    loadInvoices();
  }, [filter]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const status = filter === 'all' ? null : filter;
      const data = await clientPortalService.getInvoices(status);
      setInvoices(data.invoices || []);
      setStats({
        total: data.total || 0,
        total_outstanding: data.total_outstanding || 0,
        total_overdue: data.total_overdue || 0
      });
    } catch (err) {
      console.error('Failed to load invoices:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePayInvoice = async (invoiceId) => {
    try {
      setProcessingPayment(true);
      const result = await clientPortalService.initiatePayment(
        invoiceId,
        null,
        `${window.location.origin}/payment-success`
      );
      // Redirect to Square checkout
      window.location.href = result.checkout_url;
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to initiate payment');
      setProcessingPayment(false);
    }
  };

  const loadInvoiceDetail = async (invoiceId) => {
    try {
      const detail = await clientPortalService.getInvoiceDetail(invoiceId);
      setSelectedInvoice(detail);
    } catch (err) {
      console.error('Failed to load invoice detail:', err);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid': return <Check className="text-green-400" size={16} />;
      case 'overdue': return <AlertCircle className="text-red-400" size={16} />;
      default: return <Clock className="text-amber-400" size={16} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'overdue': return 'bg-red-500/20 text-red-300 border-red-500/30';
      case 'partial': return 'bg-amber-500/20 text-amber-300 border-amber-500/30';
      default: return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900">
      {/* Header */}
      <header className="bg-virgo-navy-800/80 border-b border-virgo-teal-700/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center space-x-4">
          <button
            onClick={() => selectedInvoice ? setSelectedInvoice(null) : navigate('/portal/dashboard')}
            className="text-virgo-teal-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={24} />
          </button>
          <div className="flex items-center space-x-3">
            <FileText className="text-cyan-400" size={24} />
            <h1 className="text-xl font-bold text-white">
              {selectedInvoice ? `Invoice ${selectedInvoice.invoice_number}` : 'Invoices'}
            </h1>
          </div>
          <a href="/portal/payments" className="text-cyan-400 hover:text-cyan-300 text-sm">
            Manage Cards & Plans
          </a>
          <div className="hidden">
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {selectedInvoice ? (
          /* Invoice Detail View */
          <div className="space-y-6">
            {/* Invoice Header */}
            <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-white">{selectedInvoice.invoice_number}</h2>
                  {selectedInvoice.case_name && (
                    <p className="text-virgo-teal-400 mt-1">{selectedInvoice.case_name}</p>
                  )}
                </div>
                <span className={`px-3 py-1 text-sm rounded-full border ${getStatusColor(selectedInvoice.status)}`}>
                  {selectedInvoice.status.toUpperCase()}
                </span>
              </div>

              <div className="grid md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-virgo-teal-500">Invoice Date</p>
                  <p className="text-white font-medium">
                    {new Date(selectedInvoice.invoice_date).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-virgo-teal-500">Due Date</p>
                  <p className={`font-medium ${selectedInvoice.is_overdue ? 'text-red-400' : 'text-white'}`}>
                    {new Date(selectedInvoice.due_date).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-virgo-teal-500">Total Amount</p>
                  <p className="text-white font-medium">${selectedInvoice.total_amount.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-virgo-teal-500">Amount Due</p>
                  <p className={`font-bold text-xl ${selectedInvoice.amount_due > 0 ? 'text-amber-400' : 'text-green-400'}`}>
                    ${selectedInvoice.amount_due.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Line Items */}
            <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 overflow-hidden">
              <div className="p-4 border-b border-virgo-navy-600">
                <h3 className="font-semibold text-white">Line Items</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-virgo-navy-800/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm text-virgo-teal-400">Description</th>
                      <th className="px-4 py-3 text-right text-sm text-virgo-teal-400">Qty</th>
                      <th className="px-4 py-3 text-right text-sm text-virgo-teal-400">Rate</th>
                      <th className="px-4 py-3 text-right text-sm text-virgo-teal-400">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedInvoice.line_items?.map((item, index) => (
                      <tr key={index} className="border-b border-virgo-navy-600">
                        <td className="px-4 py-3 text-white">{item.description}</td>
                        <td className="px-4 py-3 text-right text-virgo-teal-300">{item.quantity}</td>
                        <td className="px-4 py-3 text-right text-virgo-teal-300">${item.unit_price}</td>
                        <td className="px-4 py-3 text-right text-white font-medium">${item.amount}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-virgo-navy-800/50">
                    <tr>
                      <td colSpan="3" className="px-4 py-3 text-right text-virgo-teal-400">Subtotal</td>
                      <td className="px-4 py-3 text-right text-white">${selectedInvoice.subtotal}</td>
                    </tr>
                    {selectedInvoice.tax_amount > 0 && (
                      <tr>
                        <td colSpan="3" className="px-4 py-3 text-right text-virgo-teal-400">Tax</td>
                        <td className="px-4 py-3 text-right text-white">${selectedInvoice.tax_amount}</td>
                      </tr>
                    )}
                    <tr className="font-bold">
                      <td colSpan="3" className="px-4 py-3 text-right text-white">Total</td>
                      <td className="px-4 py-3 text-right text-white">${selectedInvoice.total_amount}</td>
                    </tr>
                    {selectedInvoice.amount_paid > 0 && (
                      <tr>
                        <td colSpan="3" className="px-4 py-3 text-right text-green-400">Paid</td>
                        <td className="px-4 py-3 text-right text-green-400">-${selectedInvoice.amount_paid}</td>
                      </tr>
                    )}
                    <tr className="font-bold text-lg">
                      <td colSpan="3" className="px-4 py-3 text-right text-amber-400">Balance Due</td>
                      <td className="px-4 py-3 text-right text-amber-400">${selectedInvoice.amount_due}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Payments */}
            {selectedInvoice.payments?.length > 0 && (
              <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-4">
                <h3 className="font-semibold text-white mb-4">Payment History</h3>
                <div className="space-y-2">
                  {selectedInvoice.payments.map((payment, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-virgo-navy-800/50 rounded-lg">
                      <div>
                        <p className="text-white">{payment.method}</p>
                        <p className="text-sm text-virgo-teal-500">{new Date(payment.date).toLocaleDateString()}</p>
                      </div>
                      <p className="text-green-400 font-medium">${payment.amount}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pay Button */}
            {selectedInvoice.amount_due > 0 && selectedInvoice.status !== 'paid' && (
              <div className="flex justify-end">
                <button
                  onClick={() => handlePayInvoice(selectedInvoice.invoice_id)}
                  disabled={processingPayment}
                  className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 font-semibold flex items-center space-x-2 shadow-lg shadow-cyan-500/30 disabled:opacity-50 transition-all"
                >
                  <CreditCard size={20} />
                  <span>{processingPayment ? 'Processing...' : `Pay $${selectedInvoice.amount_due}`}</span>
                  <ExternalLink size={16} />
                </button>
              </div>
            )}
          </div>
        ) : (
          /* Invoice List View */
          <>
            {/* Stats */}
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-4">
                <p className="text-sm text-virgo-teal-400">Total Invoices</p>
                <p className="text-2xl font-bold text-white">{stats.total}</p>
              </div>
              <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-amber-500/30 p-4">
                <p className="text-sm text-amber-400">Outstanding Balance</p>
                <p className="text-2xl font-bold text-amber-400">${stats.total_outstanding.toLocaleString()}</p>
              </div>
              <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-red-500/30 p-4">
                <p className="text-sm text-red-400">Overdue</p>
                <p className="text-2xl font-bold text-red-400">${stats.total_overdue.toLocaleString()}</p>
              </div>
            </div>

            {/* Filter */}
            <div className="mb-6">
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-4 py-2 bg-virgo-navy-700 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
              >
                <option value="all">All Invoices</option>
                <option value="sent">Pending</option>
                <option value="overdue">Overdue</option>
                <option value="partial">Partially Paid</option>
                <option value="paid">Paid</option>
              </select>
            </div>

            {/* Invoice List */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
              </div>
            ) : invoices.length === 0 ? (
              <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-12 text-center">
                <FileText className="text-virgo-teal-500 mx-auto mb-4" size={48} />
                <h2 className="text-xl font-semibold text-white mb-2">No Invoices</h2>
                <p className="text-virgo-teal-400">You don't have any invoices yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {invoices.map((invoice) => (
                  <div
                    key={invoice.invoice_id}
                    onClick={() => loadInvoiceDetail(invoice.invoice_id)}
                    className={`bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border p-6 cursor-pointer hover:border-cyan-500/50 transition-all ${
                      invoice.is_overdue ? 'border-red-500/30' : 'border-virgo-teal-700/30'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center space-x-3 mb-2">
                          {getStatusIcon(invoice.status)}
                          <h3 className="font-semibold text-white">{invoice.invoice_number}</h3>
                          <span className={`px-2 py-0.5 text-xs rounded-full border ${getStatusColor(invoice.status)}`}>
                            {invoice.status}
                          </span>
                        </div>
                        {invoice.case_name && (
                          <p className="text-sm text-virgo-teal-400">{invoice.case_name}</p>
                        )}
                        <p className="text-sm text-virgo-teal-500 mt-1">
                          Due: {new Date(invoice.due_date).toLocaleDateString()}
                        </p>
                      </div>

                      <div className="text-right">
                        <p className="text-lg font-bold text-white">${invoice.total_amount.toLocaleString()}</p>
                        {invoice.amount_due > 0 && invoice.amount_due !== invoice.total_amount && (
                          <p className="text-sm text-amber-400">Due: ${invoice.amount_due.toLocaleString()}</p>
                        )}
                      </div>
                    </div>

                    {invoice.amount_due > 0 && invoice.status !== 'paid' && (
                      <div className="mt-4 flex justify-end">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePayInvoice(invoice.invoice_id);
                          }}
                          disabled={processingPayment}
                          className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 text-sm flex items-center space-x-2 disabled:opacity-50 transition-all"
                        >
                          <CreditCard size={16} />
                          <span>Pay Now</span>
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default PortalInvoices;
